import TutorialGuidanceStepContent from '@/components/common/TutorialGuidanceStepContent.vue';
import { useFormat } from '@/composables/format';
import type { TutorialGuidanceDto, TutorialGuidanceStep } from '@onrunning/backstage-models';
import { driver, type Config, type DriveStep, type PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import { createApp, nextTick, type App } from 'vue';
import { useI18n } from 'vue-i18n';

const REFRESH_POPOVER_POSITION_DELAY = 500;

export function useTutorialGuidanceTour() {
  const { t } = useI18n();
  const { getTranslationFromTranslateField } = useFormat();
  const mountedApps: App[] = [];

  const renderStepContent = (popover: PopoverDOM, step: TutorialGuidanceStep) => {
    const container = document.createElement('div');
    popover.description.innerHTML = '';
    popover.description.appendChild(container);
    // Remove display:none that driver.js adds when description is empty
    popover.description.style.display = '';

    const app = createApp(TutorialGuidanceStepContent, {
      imageUrl: step.imageUrl,
      video: step.video,
      description: step.description ? getTranslationFromTranslateField(step.description) : '',
      title: step.title ? getTranslationFromTranslateField(step.title) : '',
      titleUrl: step.titleUrl,
      browserNotSupportedVideoText: t('tutorialGuidance.browser_not_supported_video'),
      playVideoAriaLabel: t('tutorialGuidance.play_video'),
    });

    app.mount(container);
    mountedApps.push(app);
  };

  const cleanupMountedApps = () => {
    mountedApps.forEach((app) => app.unmount());
    mountedApps.length = 0;
  };

  const positionArrowRelativeToActiveElement = (popover: PopoverDOM) => {
    const activeElement = document.querySelector('.driver-active-element');
    const arrow = popover.arrow;

    if (!activeElement || !arrow) return;

    const activeRect = activeElement.getBoundingClientRect();
    const popoverRect = popover.wrapper.getBoundingClientRect();

    // Position arrow at 15px from the top of the active element
    // Keep it as the same as the native driver.js offset
    const offset = activeRect.top + 5 - popoverRect.top;
    arrow.style.top = `${offset}px`;
    arrow.style.bottom = 'auto';
  };

  const refreshPopoverPosition = (driverObj: ReturnType<typeof driver>, popover: PopoverDOM) => {
    setTimeout(() => {
      driverObj.refresh();
      // Wait for browser to complete the refresh and reposition
      requestAnimationFrame(() => {
        positionArrowRelativeToActiveElement(popover);
      });
    }, REFRESH_POPOVER_POSITION_DELAY);
  };

  const mapStep = (step: TutorialGuidanceStep): DriveStep => ({
    element: step.element,
  });

  const transformSteps = ({
    steps = [],
  }: TutorialGuidanceDto): { driveSteps: DriveStep[]; originalSteps: TutorialGuidanceStep[] } => ({
    driveSteps: steps.map(mapStep),
    originalSteps: steps,
  });

  const startGuidanceTour = (guidance: TutorialGuidanceDto, callbacks?: Partial<Config>) => {
    const { driveSteps, originalSteps } = transformSteps(guidance);

    if (!driveSteps.length) {
      if (callbacks?.onDestroyed) {
        (callbacks.onDestroyed as () => void)();
      }

      return;
    }

    const driverObj = driver({
      showProgress: true,
      animate: true,
      steps: driveSteps,
      nextBtnText: t('tutorialGuidance.next_action'),
      prevBtnText: t('tutorialGuidance.previous_action'),
      doneBtnText: t('tutorialGuidance.done_action'),
      ...callbacks,
      onPopoverRender: (popover, opts) => {
        const activeIndex = opts.state.activeIndex ?? 0;
        const currentStep = originalSteps[activeIndex];

        if (!currentStep) return;

        popover.closeButton.innerText = '';
        popover.closeButton.style.display = 'block';

        renderStepContent(popover, currentStep);

        nextTick(() => {
          refreshPopoverPosition(driverObj, popover);
        });

        callbacks?.onPopoverRender?.(popover, opts);
      },
      onDestroyed: (element, step, opts) => {
        cleanupMountedApps();
        callbacks?.onDestroyed?.(element, step, opts);
      },
    });

    driverObj.drive();
  };

  return { startGuidanceTour };
}
